import { Mode, modes as defaultModes } from '../types/mode';
import React, { createContext, useState, useContext, ReactNode, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useToast } from './ToastContext';
import { useAuth } from './AuthContext';
import { useWindow } from './WindowContext';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { useAnalytics } from '../hooks/useAnalytics';

const MODE_STORAGE_KEY = 'studybuddy_selected_mode';

interface ModeContextProps {
  mode: Mode;
  setMode: (mode: Mode) => void;
  availableModes: Mode[];
  isModeModalOpen: boolean;
  setIsModeModalOpen: (open: boolean) => void;
  handleModeEdit: (mode: Mode) => void;
  modeToEdit: Mode | null;
  setModeToEdit: (mode: Mode | null) => void;   
  startCreateMode: () => void;
  handleSaveButtonClick: () => void;
  showModeSaveButton: boolean;
  handleDeleteMode: (mode: Mode) => void;
}

const DEFAULT_NEW_MODE_NAME = 'New Mode';

const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};


const getAllAvailableModes = (userModes?: Mode[]): Mode[] => {
  const combinedModesMap = new Map<string, Mode>();
  
  defaultModes.forEach(mode => {
    const modeWithId = mode.id ? mode : { ...mode, id: `default-${mode.name}` };
    combinedModesMap.set(modeWithId.id, modeWithId);
  });
  
  if (userModes && Array.isArray(userModes)) {
    userModes.forEach((mode: Mode) => {
      const modeWithId = mode.id ? mode : { ...mode, id: `custom-${mode.name}` };
      combinedModesMap.set(modeWithId.id, modeWithId);
    });
  }
  
  return Array.from(combinedModesMap.values());
};

const ModeContext = createContext<ModeContextProps | undefined>(undefined);

interface ModeProviderProps {
  children: ReactNode;
}

export const ModeProvider: React.FC<ModeProviderProps> = ({ children }) => {
  const { user, updateUserModes } = useAuth();
  const { trackModeChange } = useAnalytics();
  const [availableModes, setAvailableModes] = useState<Mode[]>(() => {
    return getAllAvailableModes(user?.modes);
  });
  const location = useLocation();
  const toast = useToast();
  const { openConfirmationModal } = useWindow();

  // Use localStorage to persist the selected mode
  const [storedModeData, setStoredModeData] = useLocalStorage<Mode | null>(
    MODE_STORAGE_KEY,
    null,
    {
      enabled: true,
      expiresInMs: 7 * 24 * 60 * 60 * 1000 // 7 days
    }
  );

  const [mode, setModeInternal] = useState<Mode>(() => {
    const initialModes = getAllAvailableModes(user?.modes);

    // Try to find the stored mode in available modes
    if (storedModeData) {
      const foundMode = initialModes.find(m =>
        m.id === storedModeData.id || m.name === storedModeData.name
      );
      if (foundMode) {
        return foundMode;
      }
    }

    return initialModes[0];
  });

  const [isModeModalOpen, setIsModeModalOpen] = useState(false);
  const [modeToEdit, setModeToEdit] = useState<Mode | null>(null);
  const [editingModeOriginalName, setEditingModeOriginalName] = useState<string | null>(null);
  const [originalModeSnapshotForEdit, setOriginalModeSnapshotForEdit] = useState<Mode | null>(null);

  // Ref to read current mode inside effects without adding it as a dependency
  const modeRef = useRef(mode);
  modeRef.current = mode;

  // Stable key derived from user modes to avoid re-running effects on every user object change
  const userModesKey = useMemo(() => {
    if (!user?.modes || !Array.isArray(user.modes)) return '';
    return user.modes.map(m => `${m.id ?? ''}:${m.name ?? ''}`).join('|');
  }, [user?.modes]);

  useEffect(() => {
    // Always recompute on user identity / modes change, even when modes is
    // missing — otherwise logging out or switching to a user without modes
    // leaves the previous user's custom modes in availableModes.
    const userModes = Array.isArray(user?.modes) ? user!.modes : [];
    const finalModes = getAllAvailableModes(userModes);
    setAvailableModes(finalModes);

    const current = modeRef.current;
    const currentModeStillExists = finalModes.find(m => m.id === current.id || m.name === current.name);
    if (!currentModeStillExists) {
      const fallbackMode = finalModes[0];
      setModeInternal(fallbackMode);
      setStoredModeData(fallbackMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userModesKey, user?._id]);

  // Handle when storedModeData changes (e.g., loaded from localStorage)
  useEffect(() => {
    if (storedModeData) {
      const currentModes = getAllAvailableModes(user?.modes);
      const current = modeRef.current;
      const foundMode = currentModes.find(m =>
        m.id === storedModeData.id || m.name === storedModeData.name
      );
      if (foundMode && (foundMode.id !== current.id || foundMode.name !== current.name)) {
        setModeInternal(foundMode);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedModeData]);


  useEffect(() => {
    if (!isModeModalOpen) {
      setModeToEdit(null);
      setEditingModeOriginalName(null);
      setOriginalModeSnapshotForEdit(null);
    }
  }, [isModeModalOpen]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const incomingMessage = params.get('message');
    const incomingMode = params.get('mode') || 'qa';
    if (incomingMessage) {
      const targetMode = availableModes.find(m => m.name === incomingMode || m.id === incomingMode);
      if (targetMode) {
        setModeInternal(targetMode);
        setStoredModeData(targetMode);
      }
      window.history.replaceState({}, '', location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleModeEdit = (modeToStartEditing: Mode) => {
    setModeToEdit({ ...modeToStartEditing });
    setEditingModeOriginalName(modeToStartEditing.name);
    setOriginalModeSnapshotForEdit({ ...modeToStartEditing });
    setIsModeModalOpen(true);
  };

  const setMode = (newMode: Mode) => {
    const modeExists = availableModes.some(m => m.id === newMode.id || m.name === newMode.name);
    if (modeExists) {
      const previousMode = mode;
      setModeInternal(newMode);
      setStoredModeData(newMode);
      
      // Track mode change
      trackModeChange({
        previousMode: previousMode.name || previousMode.id,
        newMode: newMode.name || newMode.id,
        isCustomMode: newMode.isCustom || false
      });
    } else {
      console.warn(`Attempted to set a mode "${newMode.name}" that is not available.`);
    }
  };

  const performOptimisticModeUpdate = async (
      updateAction: () => { updatedModes: Mode[]; newCurrentMode?: Mode },
      failureMessage: string
  ) => {
      const originalAvailableModes = [...availableModes];
      const originalMode = mode;
      let modeWasChangedByOptimisticUpdate = false;

      try {
          const { updatedModes, newCurrentMode } = updateAction();

          setAvailableModes(updatedModes);

          if (newCurrentMode) {
              // Check if the current mode is still in the updated modes list
              const currentModeExists = updatedModes.some(m => m.id === originalMode.id || m.name === originalMode.name);
              if (!currentModeExists) {
                  setModeInternal(newCurrentMode);
                  setStoredModeData(newCurrentMode);
                  modeWasChangedByOptimisticUpdate = true;
              } else if (newCurrentMode.id !== originalMode.id || newCurrentMode.name !== originalMode.name) {
                  setModeInternal(newCurrentMode);
                  setStoredModeData(newCurrentMode);
                  modeWasChangedByOptimisticUpdate = true;
              }
          }

          if (user?._id) {
            // Only send custom modes to backend, not default modes
            const customModesToSave = updatedModes.filter(m => m.isCustom);
            await updateUserModes(customModesToSave, user._id);
          }
          return true;

      } catch (error) {
          console.error('Mode update failed:', error);
          toast(failureMessage, 'error');

          setAvailableModes(originalAvailableModes);
          if (modeWasChangedByOptimisticUpdate) {
              // Ensure the original mode still exists in the rolled back modes
              const originalModeExists = originalAvailableModes.some(m => m.id === originalMode.id || m.name === originalMode.name);
              if (originalModeExists) {
                  setModeInternal(originalMode);
                  setStoredModeData(originalMode);
              } else {
                  // If original mode doesn't exist, use the first available mode
                  setModeInternal(originalAvailableModes[0]);
                  setStoredModeData(originalAvailableModes[0]);
              }
          }
          return false;
      }
  };

  const handleModeSaveInternal = async (originalName: string, dataToSave: Mode) => {
    const updateAction = () => {
      const originalMode = availableModes.find(m => m.name === originalName);
      const updatedModes = availableModes.map(m =>
        m.name === originalName
          ? { ...dataToSave, id: originalMode?.id || dataToSave.id, isCustom: m.isCustom }
          : m
      );
      let newCurrentMode: Mode | undefined = undefined;
      if (mode.name === originalName) {
        newCurrentMode = updatedModes.find(m => m.id === dataToSave.id || m.name === dataToSave.name);
      }
      return { updatedModes, newCurrentMode };
    };

    await performOptimisticModeUpdate(
      updateAction,
      'Failed to update mode'
    );
  };

  const createModeInternal = async (dataToCreate: Mode) => {
    const newModeWithCustomFlag = { ...dataToCreate, isCustom: true };

    const updateAction = () => {
      const updatedModes = [...availableModes, newModeWithCustomFlag];
      return { updatedModes, newCurrentMode: newModeWithCustomFlag };
    };

    await performOptimisticModeUpdate(
      updateAction,
      'Failed to create mode'
    );
  };

  const handleSaveButtonClick = async () => {
    if (!modeToEdit || editingModeOriginalName === null) {
        console.error("Save button clicked with invalid state:", { modeToEdit, editingModeOriginalName });
        toast('An unexpected error occurred. Please try again.', 'error');
        return;
    }

    const currentFormName = modeToEdit.name.trim();
    if (!currentFormName) {
        toast('Mode name cannot be empty.', 'error');
        return;
    }
    const modeDataForSave = { ...modeToEdit, name: currentFormName };

    if (currentFormName === DEFAULT_NEW_MODE_NAME && editingModeOriginalName === DEFAULT_NEW_MODE_NAME) {
        toast('Please choose a name for your new mode.', 'error');
        return;
    }

    const conflictingMode = availableModes.find(m => m.name === currentFormName);
    if (conflictingMode && currentFormName !== editingModeOriginalName) {
        toast(`Mode name "${currentFormName}" already exists. Please choose a different name.`, 'error');
        return;
    }

    if (editingModeOriginalName && editingModeOriginalName !== DEFAULT_NEW_MODE_NAME) {
        await handleModeSaveInternal(editingModeOriginalName, modeDataForSave);
    } else {
        await createModeInternal(modeDataForSave);
    }
  };

  const handleDeleteMode = async (modeToDelete: Mode) => {
    if (modeToDelete.name === DEFAULT_NEW_MODE_NAME) {
        setIsModeModalOpen(false);
        return;
    }
    if (!modeToDelete.isCustom) {
        toast('Cannot delete default modes', 'error');
        return;
    }

    const updateAction = () => {
        const updatedModes = availableModes.filter(m => m.id !== modeToDelete.id && m.name !== modeToDelete.name);
        let newCurrentMode: Mode | undefined = undefined;
        if (mode.id === modeToDelete.id || mode.name === modeToDelete.name) {
            newCurrentMode = updatedModes[0] || defaultModes[0];
        }
        return { updatedModes, newCurrentMode };
    };

    const confirmDelete = () => {
        performOptimisticModeUpdate(
            updateAction,
            'Failed to delete mode'
        );
    };

    openConfirmationModal(
        `Are you sure you want to delete "${modeToDelete.name}"?`,
        confirmDelete,
        'This action cannot be undone.'
    );
  };

  const startCreateMode = () => {
    const newMode: Mode = {
      id: generateUUID(),
      name: DEFAULT_NEW_MODE_NAME,
      instructions: '',
      clarityLevel: 'medium',
      thoroughnessLevel: 'medium',
      interactivityLevel: 'medium',
      isCustom: true,
    };
    setModeToEdit(newMode);
    setEditingModeOriginalName(DEFAULT_NEW_MODE_NAME);
    setOriginalModeSnapshotForEdit(null);
    setIsModeModalOpen(true);
  };

  const showModeSaveButton = (() => {
    if (!modeToEdit) return false;

    const formName = modeToEdit.name.trim();
    if (!formName) return false;

    if (editingModeOriginalName === DEFAULT_NEW_MODE_NAME) {
      return (
        formName !== DEFAULT_NEW_MODE_NAME ||
        modeToEdit.instructions !== '' ||
        modeToEdit.clarityLevel !== 'medium' ||
        modeToEdit.thoroughnessLevel !== 'medium' ||
        modeToEdit.interactivityLevel !== 'medium'
      );
    } else if (originalModeSnapshotForEdit) {
      return (
        originalModeSnapshotForEdit.name !== formName ||
        originalModeSnapshotForEdit.instructions !== modeToEdit.instructions ||
        originalModeSnapshotForEdit.clarityLevel !== modeToEdit.clarityLevel ||
        originalModeSnapshotForEdit.thoroughnessLevel !== modeToEdit.thoroughnessLevel ||
        originalModeSnapshotForEdit.interactivityLevel !== modeToEdit.interactivityLevel
      );
    }
    
    return false;
  })();

  return (
    <ModeContext.Provider value={{
      mode,
      setMode,
      availableModes,
      isModeModalOpen,
      setIsModeModalOpen,
      handleModeEdit,
      modeToEdit,
      setModeToEdit,
      startCreateMode,
      handleSaveButtonClick,
      showModeSaveButton,
      handleDeleteMode,
    }}>
      {children}
    </ModeContext.Provider>
  );
};

export const useMode = () => {
  const context = useContext(ModeContext);
  if (context === undefined) {
    throw new Error('useMode must be used within a ModeProvider');
  }
  return context;
}; 